import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Truck, ArrowRight, ShieldCheck } from "lucide-react";
import { fmtDate, today } from "@/lib/format";

/**
 * Phase 5 · Gate Inward → Provisional GRN → Final GRN.
 */
export default function GateInward() {
  const [entries, setEntries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [tab, setTab] = useState("at_gate");
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState({ date: today(), vehicle_no: "", driver_name: "", driver_phone: "", weighbridge_in: "", weighbridge_out: "", vendor_id: "", po_id: "", notes: "" });

  const load = async () => {
    const [e, v, p] = await Promise.all([
      api.get("/gate-inward").then((r) => r.data).catch(() => []),
      api.get("/parties", { params: { party_type: "vendor" } }).then((r) => r.data).catch(() => []),
      api.get("/purchase/orders").then((r) => r.data).catch(() => []),
    ]);
    setEntries(e); setVendors(v); setPos(p);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.vehicle_no) return toast.error("Vehicle number is required");
    const vendor = vendors.find((v) => v.id === form.vendor_id);
    const po = pos.find((p) => p.id === form.po_id);
    try {
      await api.post("/gate-inward", { ...form,
        vendor_name: vendor?.name, po_number: po?.po_number });
      toast.success("Gate entry recorded");
      setDrawer(false);
      setForm({ date: today(), vehicle_no: "", driver_name: "", driver_phone: "", weighbridge_in: "", weighbridge_out: "", vendor_id: "", po_id: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const promote = async (row) => {
    // Lightweight: create provisional GRN with 1-line placeholder;
    // Full line editing can be done from the GRN page.
    try {
      await api.post(`/gate-inward/${row.id}/promote-to-grn`, {
        lines: [{ description: "To be inspected", qty: 1, rate: 0, gst_rate: 0 }],
        grand_total: 0,
      });
      toast.success("Provisional GRN created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
  };

  const filtered = tab === "all" ? entries : entries.filter((e) => e.status === tab);
  const countBy = (s) => entries.filter((e) => e.status === s).length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="gate-inward-page">
      <PageHeader eyebrow="Procurement · Phase 5" title="Gate Inward"
        description="Vehicle & driver capture at the gate. Promote to Provisional GRN, then to Final GRN after QC." />

      <div className="flex justify-end">
        <Button onClick={() => setDrawer(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1" data-testid="gi-new">
          <Plus className="w-3.5 h-3.5" /> New Gate Entry
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="at_gate" className="rounded-sm" data-testid="gi-tab-at-gate">At Gate ({countBy("at_gate")})</TabsTrigger>
          <TabsTrigger value="provisional_grn" className="rounded-sm" data-testid="gi-tab-provisional">Provisional GRN ({countBy("provisional_grn")})</TabsTrigger>
          <TabsTrigger value="final_grn" className="rounded-sm" data-testid="gi-tab-final">Final GRN ({countBy("final_grn")})</TabsTrigger>
          <TabsTrigger value="all" className="rounded-sm" data-testid="gi-tab-all">All ({entries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th>Date</th><th>Gate #</th><th>Vehicle</th><th>Driver</th><th>Vendor</th><th>PO</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No entries in this bucket</td></tr>}
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`gi-row-${r.gate_entry_no}`}>
                    <td className="text-xs">{fmtDate(r.date)} {r.time}</td>
                    <td className="font-mono text-xs">{r.gate_entry_no}</td>
                    <td className="font-mono text-xs">{r.vehicle_no}</td>
                    <td className="text-sm">{r.driver_name || "—"}</td>
                    <td>{r.vendor_name || "—"}</td>
                    <td className="font-mono text-xs text-muted-foreground">{r.po_number || "—"}</td>
                    <td>
                      <span className={`text-[10px] font-mono uppercase ${
                        r.status === "final_grn" ? "text-emerald-600" :
                        r.status === "provisional_grn" ? "text-amber-600" : "text-accent"
                      }`}>{r.status.replaceAll("_", " ")}</span>
                    </td>
                    <td className="text-right">
                      {r.status === "at_gate" && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                                  onClick={() => promote(r)}
                                  data-testid={`gi-promote-${r.gate_entry_no}`}>
                          <Truck className="w-3 h-3" /> → Provisional GRN <ArrowRight className="w-3 h-3" />
                        </Button>
                      )}
                      {r.status === "provisional_grn" && (
                        <a href="/app/purchase-workflow" className="text-[11px] font-mono text-accent uppercase hover:underline">Open GRN · Do QC →</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New Gate Inward</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Vehicle #</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="gi-form-vehicle" /></div>
            <div><Label>Driver name</Label><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Driver phone</Label><Input value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>PO reference</Label>
              <Select value={form.po_id} onValueChange={(v) => setForm({ ...form, po_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Optional PO" /></SelectTrigger>
                <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Weighbridge IN (kg)</Label><Input type="number" value={form.weighbridge_in} onChange={(e) => setForm({ ...form, weighbridge_in: e.target.value })} className="rounded-sm mt-1 tabular-nums" /></div>
            <div><Label>Weighbridge OUT (kg)</Label><Input type="number" value={form.weighbridge_out} onChange={(e) => setForm({ ...form, weighbridge_out: e.target.value })} className="rounded-sm mt-1 tabular-nums" /></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="gi-form-save">Record Gate Entry</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
